/**
 * Task-dependency data layer — TanStack Query hooks over the gated
 * dependency API (`/api/teams/{teamId}/tasks/dependencies`). This is the
 * ONLY place the dependency layer talks to the server; the rendering
 * components (the canvas overlay, the drawer rows) consume these hooks and
 * never call `apiFetch` directly — the same query ⟂ view split the rest of
 * taskmap keeps (§A7).
 *
 * Issue #766 (Phase 4). A dependency edge is `from_node ──▶ to_node`, where
 * `from` COMES BEFORE `to` (i.e. `to` DEPENDS ON `from`); the arrow points at
 * the *later* task. The list is keyed `["task-dependencies", teamId]`; both
 * mutations invalidate that key on success so the canvas arrows + drawer rows
 * re-derive from one fresh source of truth.
 *
 * Gating: these hooks are only *consumed* behind `task_dependencies`
 * (off by default); the backend enforces the gate too (404 when the
 * component is off — `require_component_for_team`). The hooks degrade calmly
 * on a 404 (`retry: false`, empty list) so an undeployed/ungated endpoint
 * never crashes the editor.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema
 * types — no scheduler coupling, no rendering.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";
import { TASK_NODES_KEY } from "./taskNodes";

/** One dependency edge, as it arrives from the API. */
export type TaskDependency = components["schemas"]["TaskDependencyRead"];
type DependencyListResponse = components["schemas"]["DependencyListResponse"];
type DependencyResponse = components["schemas"]["DependencyResponse"];
type InsertNodeBetweenResponse = components["schemas"]["InsertNodeBetweenResponse"];

/** Cache key for a team's dependency edge list. Exported for tests + invalidation. */
export const TASK_DEPENDENCIES_KEY = (teamId: string) => ["task-dependencies", teamId] as const;

const STALE_TIME_MS = 30_000;

function dependenciesUrl(teamId: string): string {
  return `/api/teams/${teamId}/tasks/dependencies`;
}

/**
 * The whole-team dependency edge list (the DAG layered on the tree, §A5).
 * Whole-team visible per §A8 — the same fetch for leads and members; per-role
 * *write* gating is enforced server-side. Returns `[]` while loading / on
 * error so the consumer never has to null-check.
 */
export function useTaskDependencies(teamId: string | undefined): {
  dependencies: TaskDependency[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: TASK_DEPENDENCIES_KEY(teamId ?? ""),
    enabled: Boolean(teamId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<DependencyListResponse>(dependenciesUrl(teamId as string)),
  });
  // Defensive: a non-array body (errored / unexpected response) degrades to an
  // empty layer rather than throwing the canvas overlay.
  const dependencies = Array.isArray(query.data?.dependencies) ? query.data.dependencies : [];
  return { dependencies, isLoading: query.isLoading, isError: query.isError };
}

interface AddDependencyArgs {
  /** The earlier task — `from` COMES BEFORE `to`. */
  fromNodeId: string;
  /** The later (dependent) task — the arrow points here. */
  toNodeId: string;
}

/**
 * Draw a `from ──▶ to` dependency edge (POST). Idempotent server-side
 * (re-posting an existing pair returns the existing edge); a self-loop or an
 * edge that would close a directed cycle is rejected with 422 — the caller
 * handles that calmly (no crash, factual notice). Invalidates the list.
 */
export function useAddTaskDependency(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fromNodeId, toNodeId }: AddDependencyArgs) =>
      apiFetch<DependencyResponse>(dependenciesUrl(teamId as string), {
        method: "POST",
        body: JSON.stringify({ from_node_id: fromNodeId, to_node_id: toNodeId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_DEPENDENCIES_KEY(teamId ?? "") }),
  });
}

/**
 * Remove a dependency edge by its id (DELETE → 204). Invalidates the list so
 * the arrow + the drawer rows drop on success.
 */
export function useRemoveTaskDependency(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dependencyId: string) =>
      apiFetch<void>(`${dependenciesUrl(teamId as string)}/${dependencyId}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_DEPENDENCIES_KEY(teamId ?? "") }),
  });
}

interface SetDependencyNoteArgs {
  /** The edge to annotate. */
  dependencyId: string;
  /**
   * The free-text label for what KIND of link this is ("what kind of link",
   * e.g. "needs sign-off first") — about the WORK relationship, never a person
   * (§A9). Empty/whitespace clears it; pass `null` to clear explicitly. Max 500
   * chars (the server caps it too).
   */
  note: string | null;
}

/**
 * Set / clear a dependency edge's free-text note (PATCH → `{ dependency }`,
 * #929). The note labels what the link MEANS ("needs sign-off first"); it is
 * never about a person. The server emits no event for this (a quiet edit), so
 * we only invalidate the dependency list — the canvas label + the popover
 * re-derive from one fresh source. An empty trimmed note is sent as `null`
 * (clears the label).
 */
export function useSetDependencyNote(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dependencyId, note }: SetDependencyNoteArgs) => {
      const trimmed = note?.trim() ?? "";
      return apiFetch<DependencyResponse>(`${dependenciesUrl(teamId as string)}/${dependencyId}`, {
        method: "PATCH",
        body: JSON.stringify({ note: trimmed.length > 0 ? trimmed : null }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_DEPENDENCIES_KEY(teamId ?? "") }),
  });
}

interface InsertNodeBetweenArgs {
  /** The edge being split (`from ──▶ to`). */
  dependencyId: string;
  /** The inserted task's title. */
  title: string;
}

/**
 * Insert a new task BETWEEN an edge's endpoints (POST insert-node → 201
 * `{ node, dependencies }`, #929). Atomically creates a node (a sibling of the
 * downstream `to` node) and rewires `from ──▶ to` into `from ──▶ new ──▶ to`
 * (the original edge is removed) in one transaction.
 *
 * Invalidates BOTH the dependency list (the rewired edges) AND the task-nodes
 * list (the new node) so the canvas redraws with the inserted node + its two
 * surviving edges without a full reload.
 */
export function useInsertNodeBetween(teamId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dependencyId, title }: InsertNodeBetweenArgs) =>
      apiFetch<InsertNodeBetweenResponse>(
        `${dependenciesUrl(teamId as string)}/${dependencyId}/insert-node`,
        {
          method: "POST",
          body: JSON.stringify({ title }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TASK_DEPENDENCIES_KEY(teamId ?? "") });
      void qc.invalidateQueries({ queryKey: TASK_NODES_KEY(teamId ?? "") });
    },
  });
}
